import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaArrowLeft, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaMoneyBillWave,
  FaUser,
  FaCalendarAlt,
  FaProjectDiagram,
  FaClipboardList,
  FaFileInvoice,
  FaCommentDots,
  FaInfoCircle,
  FaExclamationTriangle,
  FaPaperclip,
  FaClock,
  FaCircle
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';
import { formatDateTime } from '../../utils/dateUtils';

// Styled Components
const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: #F68B21;
    transform: translateX(-3px);
  }
`;

const RequestHeader = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  position: relative;
  
  h1 {
    font-size: 1.8rem;
    color: #141340;
    margin: 0 0 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  .request-id {
    font-size: 1rem;
    color: #6c757d;
    margin-bottom: 1.5rem;
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  position: absolute;
  top: 2rem;
  right: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => {
    switch (props.status.toLowerCase()) {
      case 'pending':
        return `
          background: rgba(255, 193, 7, 0.1);
          color: #ffc107;
        `;
      case 'approved':
        return `
          background: rgba(40, 167, 69, 0.1);
          color: #28a745;
        `;
      case 'rejected':
        return `
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        `;
      case 'paid':
        return `
          background: rgba(23, 162, 184, 0.1);
          color: #17a2b8;
        `;
      default:
        return `
          background: rgba(108, 117, 125, 0.1);
          color: #6c757d;
        `;
    }
  }}
`;

const RequestInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  margin-bottom: 2rem;
  
  h3 {
    margin: 0 0 1.5rem;
    font-size: 1.2rem;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  
  .label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 1rem;
    color: #141340;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
      font-size: 0.9rem;
    }
  }
  
  &.amount {
    .value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #F68B21;
    }
  }
`;

const ItemsList = styled.div`
  margin-top: 1.5rem;
`;

const ItemCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  
  .item-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    
    .item-name {
      font-weight: 500;
      color: #141340;
    }
    
    .item-amount {
      font-weight: 600;
      color: #F68B21;
    }
  }
  
  .item-description {
    font-size: 0.9rem;
    color: #6c757d;
  }
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const AttachmentCard = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: #141340;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  .icon {
    color: #F68B21;
  }
  
  &:hover {
    background: rgba(246, 139, 33, 0.1);
    transform: translateY(-2px);
  }
`;

const ApprovalTimeline = styled.div`
  margin-top: 1.5rem;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    top: 2rem;
    left: 0.85rem;
    width: 2px;
    height: calc(100% - 1rem);
    background: #e9ecef;
  }
  
  .timeline-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
    
    &.pending {
      background: rgba(255, 193, 7, 0.1);
      color: #ffc107;
    }
    
    &.approved {
      background: rgba(40, 167, 69, 0.1);
      color: #28a745;
    }
    
    &.rejected {
      background: rgba(220, 53, 69, 0.1);
      color: #dc3545;
    }
    
    &.current {
      background: rgba(246, 139, 33, 0.1);
      color: #F68B21;
    }
    
    &.completed {
      background: rgba(23, 162, 184, 0.1);
      color: #17a2b8;
    }
  }
  
  .timeline-content {
    flex: 1;
    
    .timeline-title {
      font-weight: 500;
      color: #141340;
      margin-bottom: 0.25rem;
    }
    
    .timeline-meta {
      display: flex;
      gap: 1rem;
      font-size: 0.85rem;
      color: #6c757d;
      margin-bottom: 0.5rem;
    }
    
    .timeline-comment {
      font-size: 0.9rem;
      color: #495057;
      background: #f8f9fa;
      border-radius: 8px;
      padding: 0.75rem 1rem;
    }
    
    .timeline-status {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }
    
    .status-badge {
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.8rem;
      font-weight: 600;
      
      &.success {
        background: #d4edda;
        color: #155724;
      }
      
      &.danger {
        background: #f8d7da;
        color: #721c24;
      }
      
      &.warning {
        background: #fff3cd;
        color: #856404;
      }
      
      &.primary {
        background: #d1ecf1;
        color: #0c5460;
      }
      
      &.secondary {
        background: #e2e3e5;
        color: #383d41;
      }
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ApproveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const RejectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: white;
  color: #dc3545;
  border: 2px solid #dc3545;
  border-radius: 8px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    background: rgba(220, 53, 69, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(220, 53, 69, 0.2);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const CommentSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  margin-top: 2rem;
  
  h3 {
    margin: 0 0 1.5rem;
    font-size: 1.2rem;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  textarea {
    width: 100%;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    font-size: 0.95rem;
    resize: vertical;
    min-height: 100px;
    margin-bottom: 1rem;
    
    &:focus {
      outline: none;
      border-color: #F68B21;
      box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
    }
  }
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  transform: translateY(0);
  transition: all 0.3s ease;
  
  h3 {
    margin: 0 0 1.5rem;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .icon {
      color: #F68B21;
    }
    
    &.reject {
      .icon {
        color: #dc3545;
      }
    }
  }
  
  p {
    color: #495057;
    margin-bottom: 1.5rem;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    
    button {
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &.cancel {
        background: #f8f9fa;
        color: #6c757d;
        border: none;
        
        &:hover {
          background: #e9ecef;
        }
      }
      
      &.confirm {
        background: #F68B21;
        color: white;
        border: none;
        
        &:hover {
          background: #e67c1a;
        }
        
        &.reject {
          background: #dc3545;
          
          &:hover {
            background: #c82333;
          }
        }
      }
    }
  }
`;



const FundRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [fundRequest, setFundRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  // Dialog state
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };
  
  useEffect(() => {
    const fetchFundRequest = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        console.log('Fetching fund request with ID:', id);
        
        const response = await apiService.getFundRequest(id);
        console.log('Fund request response:', response);
        
        if (response.success && response.data) {
          setFundRequest(response.data);
        } else {
          console.error('Fund request not found or no access');
          setFundRequest(null);
        }
      } catch (error) {
        console.error('Error fetching fund request:', error);
        setFundRequest(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFundRequest();
  }, [id]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!fundRequest) {
    return (
      <div>
        <h2>Fund Request Not Found</h2>
        <p>The requested fund request could not be found.</p>
        <BackLink to="/manager/fund-requests">
          <IconWrapper icon={FaArrowLeft} />
          Back to Fund Requests
        </BackLink>
      </div>
    );
  }
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  const getTimelineIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return FaCheckCircle;
      case 'current':
        return FaInfoCircle;
      case 'rejected':
        return FaTimesCircle;
      default:
        return FaCircle;
    }
  };

  const canApproveOrReject = (): boolean => {
    if (!user || !fundRequest) return false;
    
    // Find the current pending approval step
    const pendingStep = fundRequest.approvalChain?.find((step: any) => step.status === 'Pending');
    if (!pendingStep) return false;
    
    // Check if the current user's role matches the role needed for approval
    return pendingStep.role === user.role;
  };

  const getDisplayStatus = (step: any) => {
    // If Accountant approved, show "PAID" instead of "Approved"
    if (step.role === 'Accountant' && step.status === 'Approved') {
      return 'PAID';
    }
    return step.status;
  };

  const getApprovalStepIcon = (status: string) => {
    switch (status) {
      case 'Approved': return <IconWrapper icon={FaCheckCircle} />;
      case 'Rejected': return <IconWrapper icon={FaTimesCircle} />;
      case 'Pending': return <IconWrapper icon={FaClock} />;
      case 'INITIATED': return <IconWrapper icon={FaUser} />;
      case 'PAID': return <IconWrapper icon={FaMoneyBillWave} />;
      default: return <IconWrapper icon={FaCircle} />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'PAID': return 'success';
      case 'Rejected': return 'danger';
      case 'Pending': return 'warning';
      case 'INITIATED': return 'primary';
      default: return 'secondary';
    }
  };
  
  const handleApprove = async () => {
    if (!comment.trim()) {
      showDialog('Validation Error', 'Please add a comment before approving', 'warning');
      return;
    }
    
    try {
      const approvalData = {
        action: 'approve',
        comments: comment
      };

      const response = await apiService.approveFundRequest(fundRequest.id, approvalData);
      
      if (response.success) {
        showDialog('Success', 'Fund request approved successfully!', 'success');
        setShowApproveModal(false);
        // Refresh the request data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to approve fund request');
      }
    } catch (error: any) {
      console.error('Error approving fund request:', error);
      showDialog('Error', `Error approving fund request: ${error.message}`, 'error');
    }
  };
  
  const handleReject = async () => {
    if (!comment.trim()) {
      showDialog('Validation Error', 'Please add a comment before rejecting', 'warning');
      return;
    }
    
    try {
      const rejectionData = {
        action: 'reject',
        comments: comment
      };

      const response = await apiService.approveFundRequest(fundRequest.id, rejectionData);
      
      if (response.success) {
        showDialog('Success', 'Fund request rejected successfully!', 'success');
        setShowRejectModal(false);
        // Refresh the request data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to reject fund request');
      }
    } catch (error: any) {
      console.error('Error rejecting fund request:', error);
      showDialog('Error', `Error rejecting fund request: ${error.message}`, 'error');
    }
  };
  
  return (
    <PageContainer>
      <BackLink to="/manager/fund-requests">
        <IconWrapper icon={FaArrowLeft} />
        Back to Fund Requests
      </BackLink>
      
      <RequestHeader>
        <h1>
          <IconWrapper icon={FaMoneyBillWave} className="icon" />
          Fund Request - {fundRequest.projectName}
        </h1>
        <div className="request-id">Request ID: {fundRequest.id}</div>
        
        <StatusBadge status={fundRequest.status.toLowerCase()}>
          {(fundRequest.status === 'Submitted' || fundRequest.status === 'Under Review') && <IconWrapper icon={FaClock} />}
          {fundRequest.status === 'Approved' && <IconWrapper icon={FaCheckCircle} />}
          {fundRequest.status === 'Rejected' && <IconWrapper icon={FaTimesCircle} />}
          {fundRequest.status === 'Paid' && <IconWrapper icon={FaMoneyBillWave} />}
          {fundRequest.status}
        </StatusBadge>
      </RequestHeader>
      
      <RequestInfoGrid>
        <InfoCard>
          <h3>
            <IconWrapper icon={FaClipboardList} className="icon" />
            Request Details
          </h3>
          
          <InfoItem className="amount">
            <div className="label">Total Amount</div>
            <div className="value">{formatCurrency(fundRequest.amount)}</div>
          </InfoItem>
          
          <InfoItem>
            <div className="label">Date Requested</div>
            <div className="value">
              <IconWrapper icon={FaCalendarAlt} className="icon" />
              {formatDateTime(fundRequest.createdAt)}
            </div>
          </InfoItem>
          
          <InfoItem>
            <div className="label">Requested By</div>
            <div className="value">
              <IconWrapper icon={FaUser} className="icon" />
              {fundRequest.requesterId?.name || fundRequest.requester} ({fundRequest.requesterId?.role || 'Unknown'})
            </div>
          </InfoItem>
          
          <InfoItem>
            <div className="label">Project</div>
            <div className="value">
              <IconWrapper icon={FaProjectDiagram} className="icon" />
              {fundRequest.projectName}
            </div>
          </InfoItem>
          
          <InfoItem>
            <div className="label">Justification</div>
            <div className="value" style={{ display: 'block' }}>
              {fundRequest.justification}
            </div>
          </InfoItem>
        </InfoCard>
        
        <InfoCard>
          <h3>
            <IconWrapper icon={FaFileInvoice} className="icon" />
            Request Items
          </h3>
          
          <ItemsList>
            {fundRequest.items?.map((item: any, index: number) => (
              <ItemCard key={index}>
                <div className="item-header">
                  <div className="item-name">{item.description}</div>
                  <div className="item-amount">{formatCurrency(item.totalPrice)}</div>
                </div>
                <div className="item-description">
                  Quantity: {item.quantity} × {formatCurrency(item.unitPrice)} | Category: {item.category}
                </div>
              </ItemCard>
            )) || []}
          </ItemsList>
          
          <h3 style={{ marginTop: '2rem' }}>
            <IconWrapper icon={FaPaperclip} className="icon" />
            Attachments
          </h3>
          
          <AttachmentsList>
            {fundRequest.documents?.map((document: any, index: number) => (
              <AttachmentCard href={document.url || "#"} key={document._id || index}>
                <IconWrapper icon={FaPaperclip} className="icon" />
                {document.name}
              </AttachmentCard>
            )) || []}
          </AttachmentsList>
        </InfoCard>
      </RequestInfoGrid>
      
      <InfoCard>
        <h3>
          <IconWrapper icon={FaClipboardList} className="icon" />
          Approval Timeline
        </h3>
        
        <ApprovalTimeline>
          {fundRequest.approvalChain?.map((step: any, index: number) => (
            <TimelineItem key={index}>
              <div className={`timeline-icon ${step.status || 'pending'}`}>
                {getApprovalStepIcon(getDisplayStatus(step))}
              </div>
              <div className="timeline-content">
                <div className="timeline-title">{step.role}</div>
                <div className="timeline-status">
                  <span className={`status-badge ${getStatusVariant(getDisplayStatus(step))}`}>
                    {getDisplayStatus(step)}
                  </span>
                  {step.userName && <span> by {step.userName}</span>}
                </div>
                {step.timestamp && (
                  <div className="timeline-meta">
                    <div>{formatDateTime(step.timestamp)}</div>
                  </div>
                )}
                {step.comments && <div className="timeline-comment">{step.comments}</div>}
                {step.status === 'Pending' && step.role === user?.role && (
                  <div className="timeline-comment" style={{ background: 'rgba(246, 139, 33, 0.1)', color: '#F68B21' }}>
                    <IconWrapper icon={FaInfoCircle} style={{ marginRight: '0.5rem' }} />
                    Awaiting your approval
                  </div>
                )}
              </div>
            </TimelineItem>
          ))}
        </ApprovalTimeline>
      </InfoCard>
      
      {canApproveOrReject() && (
        <>
          <CommentSection>
            <h3>
              <IconWrapper icon={FaCommentDots} className="icon" />
              Your Comment
            </h3>
            
            <textarea
              placeholder="Add your comments or notes about this request..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            
            <ActionButtons>
              <RejectButton onClick={() => setShowRejectModal(true)}>
                <IconWrapper icon={FaTimesCircle} />
                Reject Request
              </RejectButton>
              <ApproveButton onClick={() => setShowApproveModal(true)}>
                <IconWrapper icon={FaCheckCircle} />
                Approve Request
              </ApproveButton>
            </ActionButtons>
          </CommentSection>
          
          <ModalOverlay isOpen={showApproveModal}>
            <Modal>
              <h3>
                <IconWrapper icon={FaCheckCircle} className="icon" />
                Confirm Approval
              </h3>
              <p>
                Are you sure you want to approve this fund request for {formatCurrency(fundRequest.amount)}? 
                This will forward the request to the next approval step.
              </p>
              <div className="modal-actions">
                <button className="cancel" onClick={() => setShowApproveModal(false)}>Cancel</button>
                <button className="confirm" onClick={handleApprove}>Approve</button>
              </div>
            </Modal>
          </ModalOverlay>
          
          <ModalOverlay isOpen={showRejectModal}>
            <Modal>
              <h3 className="reject">
                <IconWrapper icon={FaTimesCircle} className="icon" />
                Confirm Rejection
              </h3>
              <p>
                Are you sure you want to reject this fund request? 
                This action cannot be undone and will stop the approval process.
              </p>
              <div className="modal-actions">
                <button className="cancel" onClick={() => setShowRejectModal(false)}>Cancel</button>
                <button className="confirm reject" onClick={handleReject}>Reject</button>
              </div>
            </Modal>
          </ModalOverlay>
        </>
      )}
      
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />
    </PageContainer>
  );
};

export default FundRequestDetail; 